import * as React from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/solid";

import {
  calculatePerformanceLevel,
  calculateScore,
  calculateScoreAfterMitigation,
  plrStartingPoint,
} from "./utils";
import { Options } from "./CardOptions";
import { Risk as RiskInterface } from "./types";
import { useConfirmation } from "context/Confirmation";
import { useDeleteRisk } from "./hooks/useUpdateRisk";
import Collapsible from "components/Collapsible/Collapsible";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { Feature, useFeatureFlags } from "context/FeatureFlag";

type RiskProps = { risk: RiskInterface; projectId: string; idx: number };

export const Risk: React.FunctionComponent<RiskProps> = (props) => {
  const { risk, projectId } = props;

  const history = useHistory();
  const { t } = useTranslation();
  const { isFeatureEnabled } = useFeatureFlags();
  const { openConfirmation } = useConfirmation();
  const [showOptions, setShowOptions] = React.useState(false);
  const optionsRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Add click outside listener to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showOptions &&
        optionsRef.current &&
        buttonRef.current &&
        !optionsRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOptions]);

  const totalRisk = calculateScore(risk.assessment);
  const deleteRisk = useDeleteRisk(projectId, risk.resource_id);

  const handleDelete = () => {
    openConfirmation(deleteRisk, {
      label: t("form.basic.label"),
      subline: t("steps.risks.confirmation.subline"),
    });
  };

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8 w-full">
      <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
        <div className="-ml-4 -mt-4 flex justify-between items-center flex-wrap sm:flex-nowrap">
          <div className="ml-4 mt-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t("form.mitigations.risk")} #{props.idx + 1}{" "}
              {risk.readable_name ? `- ${risk.readable_name}` : ""}
            </h3>
            <div className="mt-1 text-sm text-gray-500">
              <div>
                <span>
                  <b>{t("form.mitigations.description")}:</b> {risk.description}
                </span>
              </div>
              <div>
                <span>
                  <b>{t("form.mitigations.risk_type")}:</b>{" "}
                  {t("form.risk.options.group." + risk.group_id)}
                </span>
              </div>

              <div>
                <span className="flex items-center">
                  <b>{t("form.mitigations.mitigated")}:</b>{" "}
                  {risk.fully_mitigated ? (
                    <CheckCircleIcon
                      className="ml-1 h-4 w-4 text-green-400"
                      aria-hidden="true"
                    />
                  ) : (
                    <XCircleIcon
                      className="ml-1 h-4 w-4 text-red-600"
                      aria-hidden="true"
                    />
                  )}
                </span>
              </div>

              {risk.reasoning && (
                <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
                  <span className="flex items-center text-indigo-700 font-medium text-xs mb-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {t("form.mitigations.ai_reasoning")}
                  </span>
                  <p className="text-sm text-indigo-900 whitespace-pre-wrap">
                    {risk.reasoning}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="ml-4 mt-4 flex-shrink-0 space-x-1 self-start flex">
            <PrimaryButton
              onClick={() =>
                history.push(
                  `/projects/${projectId}/risks/${risk.resource_id}/edit`,
                )
              }
            >
              {t("form.mitigations.edit")}
            </PrimaryButton>

            <div className="relative flex justify-end items-center">
              <button
                type="button"
                className="w-8 h-8 inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                id="standard-options-menu-0"
                aria-expanded="false"
                aria-haspopup="true"
                ref={buttonRef}
                onClick={() => {
                  setShowOptions(!showOptions);
                }}
              >
                <span className="sr-only">Open options</span>
                <svg
                  className="w-5 h-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
              {showOptions && (
                <div ref={optionsRef}>
                  <Options
                    onClose={() => setShowOptions(false)}
                    onDelete={handleDelete}
                    projectCRN={projectId}
                    risk={props.risk}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="ml-4">
        <Collapsible
          useCustomStyles
          label={t("form.mitigations.risk_reducing_mitigations")}
          child={
            <div className="py-2 px-2">
              <div className="pb-3">
                <div>
                  <span className="text-md font-medium text-gray-500">
                    {t("form.mitigations.initial_risk")}:{" "}
                  </span>
                  <span>{totalRisk}</span>
                </div>
                <div>
                  {risk.mitigations?.length > 1 && (
                    <>
                      <span className="text-md font-medium text-gray-500">
                        {t("form.mitigations.risks_after_mitigations")}:
                      </span>
                      <span>
                        {calculateScoreAfterMitigation(
                          risk,
                          risk.mitigations.length - 1,
                        )}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {(risk.mitigations?.length &&
                risk.mitigations.map((mitigation, idx) => {
                  const pictos = mitigation.pictogram?.split(";") || [];
                  const pictoIds = pictos.map((picto) => {
                    const pictoId = picto.match(/^[A-Z]\d{3}/);
                    return pictoId ? pictoId[0] : null;
                  });

                  return (
                    <div key={idx} className="pb-6">
                      <h3 className="text-md font-bold">
                        {t("form.mitigations.mitigation")} #{idx + 1}
                      </h3>
                      <dl
                        key={mitigation.id}
                        className="grid grid-cols-3 gap-x-4 gap-y-8 sm:grid-cols-3"
                      >
                        <div>
                          <div className="sm:col-span-1">
                            <dt className="text-md font-medium text-gray-500">
                              {t("form.mitigations.type")}
                            </dt>
                            <dd className="mb-1 text-sm text-gray-900">
                              {mitigation.type}{" "}
                              {mitigation.type === "ESS" && (
                                <i>
                                  (
                                  {t(
                                    "form.mitigations.performance_level_required",
                                  )}{" "}
                                  {calculatePerformanceLevel(
                                    plrStartingPoint(
                                      isFeatureEnabled!(
                                        Feature.PlrRelativeToMeasures,
                                      ),
                                      risk,
                                      risk.mitigations,
                                      idx,
                                    ),
                                  )}
                                  )
                                </i>
                              )}
                              {mitigation.type === "PIK" && (
                                <>
                                  {mitigation.pictogram && (
                                    <div className="sm:col-span-1">
                                      <dt className="text-sm font-medium text-gray-500">
                                        {t("form.mitigations.pictogram")}
                                      </dt>
                                      <dd className="mb-1 text-sm text-gray-900">
                                        {pictoIds.map((id) => (
                                          <img
                                            src={
                                              process.env.REACT_APP_DATA_URL +
                                              "/pictograms/svgs/" +
                                              id
                                            }
                                            className="h-8 inline ml-2"
                                            alt=""
                                          />
                                        ))}
                                      </dd>
                                    </div>
                                  )}
                                </>
                              )}
                            </dd>
                          </div>
                        </div>

                        <div className="sm:col-span-1">
                          <dt className="text-md font-medium text-gray-500">
                            {t("form.mitigations.description")}
                          </dt>
                          <dd className="mb-1 text-sm text-gray-900">
                            {mitigation.description}
                          </dd>
                        </div>

                        <div className="sm:col-span-1">
                          <dt className="text-md font-medium text-gray-500">
                            {t("form.mitigations.risks_after_mitigations")}
                          </dt>
                          <dd className="mb-1 text-sm text-gray-900">
                            <span className="font-bold">Score:</span>{" "}
                            <span>
                              {calculateScoreAfterMitigation(risk, idx)}
                            </span>
                            <br />
                            <span className="font-bold">Restrisiko: </span>
                            <span>{mitigation.left_risk}</span>
                          </dd>
                        </div>
                      </dl>

                      {mitigation.reasoning && (
                        <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
                          <span className="flex items-center text-indigo-700 font-medium text-xs mb-1">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                                clipRule="evenodd"
                              />
                            </svg>
                            {t("form.mitigations.ai_reasoning")}
                          </span>
                          <p className="text-sm text-indigo-900 whitespace-pre-wrap">
                            {mitigation.reasoning}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })) || (
                <p className="mt-1 text-sm text-gray-900">
                  {t("form.mitigations.none")}
                </p>
              )}
            </div>
          }
        />
      </div>
    </div>
  );
};
